import { useCallback, useState } from "react";
import { setSupportTenant } from "../services/ApiService";
import { applySupportTarget } from "../services/supportTenantRuntime";
import { useStoreState } from "../store";
import { useAuth } from "../utility/useAuth";
import { isSupportAutoTenantEnabled } from "../utility/supportTenantAccess";
import { resolveSupportTarget } from "../utility/supportTenant";
import { updateCachedSupportTenant } from "../utility/authCache";
import { logger } from "../utility/logger";
import { notify } from "../utility/notify";
import { ACCESS_ERRORS } from "../utility/errorCatalog";
import { buildReportedError, serverMessageOf, statusOf } from "../utility/reportError";
import { useAccess } from "./AccessProvider";

/**
 * Moves a CORP/CORP2 session to another organisation, or back to the user's own. One
 * implementation for the org switcher (`OrgChangePage`) and for the way back from a switch the
 * server refused (`AccessGate`), so the two cannot disagree about who the user connects as.
 *
 * `switchTo` resolves `false` when the code is not a target for this user and nothing was
 * changed, and `true` once the session has moved. `saving` is true while the saved tenant is
 * being written.
 */
export function useOrganizationSwitch() {
  const auth = useAuth();
  const { organizationSwitched } = useAccess();
  const user = useStoreState((s: any) => s.user);
  const [saving, setSaving] = useState(false);

  const switchTo = useCallback(
    async (orgCode: string): Promise<boolean> => {
      // One resolver for both entry points (here and PrivateRoute's login restore), so they
      // cannot disagree about who the user connects as. It also decides the identity: another
      // org's environment ⇒ that org's support user; their OWN dedicated environment ⇒ no
      // override, they act as themselves. Null means "not a corp user / unknown org" — leave
      // the session alone (the route's switchOrganization guard already keeps non-corp users
      // off the switcher).
      // A support-bootstrap user has no row of their own, so "their own environment" does not
      // exist for them: switching back to CORP has to re-apply SUPPORT_CORPUSER rather than
      // drop the override, or they land in CORP with nothing to see.
      const bootstrapped = user?.info?.supportBootstrap === true;

      const target = resolveSupportTarget({
        whoto: user?.info?.whoto,
        orgCode,
        unprovisioned: bootstrapped,
      });

      if (!target) return false;

      // Points the API + realtime layers at the org's region, relabels the header, and sets
      // (or clears) the impersonation.
      applySupportTarget(target);

      // In the same handler as the move, so no render shows the previous organisation's
      // grants: a new scope drops them and reads GET api/me again. Whether the server honoured
      // the switch comes back as the provider's `switchFailed`.
      organizationSwitched();

      // Survive a page reload. Purely client-side session state, so it is written for EVERY
      // switch — including a bootstrapped user's, who has no server-side place to record one.
      updateCachedSupportTenant(target.code);

      // Remember it as this user's last active tenant so their next login lands here
      // (feature-flagged; the backend enforces CORP-only independently). setSupportTenant
      // deliberately targets the user's OWN region and omits the ambient override, so the
      // order here is safe despite the impersonation applied just above.
      //
      // Skipped for a bootstrapped user: the column is dbo.Users.LastSupportTenant on the row
      // they do not have, so the request could only fail and toast an error about something
      // that is in fact working. Their org choice lasts the session, not across logins.
      if (!bootstrapped && isSupportAutoTenantEnabled()) {
        setSaving(true);
        try {
          await setSupportTenant(auth, target.code);
        } catch (error) {
          // The switch itself worked — only remembering it failed, so carry on either way
          // rather than trapping the user. That is also why the sentence survives the coding:
          // the catalogue's wording would read as "the switch failed", which is the one thing
          // that did not happen.
          const reported = buildReportedError(ACCESS_ERRORS.TENANT, error, {
            resource: target.code,
            serverMessage: serverMessageOf(error),
            status: statusOf(error),
          });
          logger.error(reported.diagnostics, error);
          notify.error(
            `Switched to ${target.code}, but it could not be saved as your default organization. ` +
              `(${reported.code})`
          );
        } finally {
          setSaving(false);
        }
      }

      return true;
    },
    [auth, organizationSwitched, user]
  );

  return { switchTo, saving };
}
